import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import type { Reference } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

/**
 * Hooks for the redesigned playbook rules, which take an ordered list of
 * positions and sit under the same `PlaybookRuleCategory` rows as the original
 * ones. The category mutations themselves live in `playbook-rule-category.ts` and are
 * shared with the document editor.
 *
 * Most mutations return the row they changed, normalized by id, so the cache
 * updates itself. Only the ones that add or remove a row from a list need a
 * manual write, and those are the ones with an `update` below.
 */
export function useCreatePlaybookRule() {
  const [createPlaybookRule, { loading }] = Apollo.useCreatePlaybookRuleMutation();
  const safeCreatePlaybookRule = useCallback(
    (input: Apollo.CreatePlaybookRuleInput) => {
      return createPlaybookRule({
        update(cache, { data }) {
          if (!data?.createPlaybookRule) return;

          const ruleRef = cache.writeFragment({
            data: data.createPlaybookRule,
            fragment: Apollo.PlaybookRuleSimpleFragmentDoc,
            fragmentName: Apollo.namedOperations.Fragment.PlaybookRuleSimple,
          });
          if (!ruleRef) return;

          cache.modify({
            fields: {
              // Appended, because the server ranks a new rule last in its category.
              rules: (refs: readonly Reference[] = []) => [...refs, ruleRef],
            },
            id: cache.identify({ __typename: 'PlaybookRuleCategory', id: input.categoryId }),
          });
        },
        variables: { input },
      });
    },
    [createPlaybookRule]
  );

  return { createPlaybookRule: safeCreatePlaybookRule, loading };
}

export function useUpdatePlaybookRule() {
  const [updatePlaybookRule, { loading }] = Apollo.useUpdatePlaybookRuleMutation();
  const safeUpdatePlaybookRule = useCallback(
    (input: Apollo.UpdatePlaybookRuleInput) => {
      return updatePlaybookRule({ variables: { input } });
    },
    [updatePlaybookRule]
  );

  return { loading, updatePlaybookRule: safeUpdatePlaybookRule };
}

/**
 * Moves a rule within its category or into another one.
 *
 * The cached `rules` lists hold the server's order and nothing re-sorts them
 * by rank on the client, so the returned rule — which only updates the rule's
 * own fields — is not enough: the rule is spliced out of its old list and into
 * the target list at `toIndex` by hand. `toIndex` counts among the category's
 * *other* rules, which is why the rule is removed before it is inserted.
 *
 * Pass `optimisticRule` (the rule as it will read after the move, including
 * the rank it is expected to get) to apply that splice straight away, so a
 * dropped rule stays where it was dropped instead of snapping back until the
 * server replies. The server's reply then replaces the guessed rank.
 */
export function useMovePlaybookRule() {
  const [movePlaybookRule, { loading }] = Apollo.useMovePlaybookRuleMutation();
  const safeMovePlaybookRule = useCallback(
    (options: {
      fromCategoryId: UUID;
      input: Apollo.MovePlaybookRuleInput;
      optimisticRule?: Apollo.PlaybookRuleSimpleFragment;
    }) => {
      const { fromCategoryId, input, optimisticRule } = options;
      const { categoryId, ruleId, toIndex } = input;

      return movePlaybookRule({
        optimisticResponse: optimisticRule
          ? { __typename: 'Mutation', movePlaybookRule: optimisticRule }
          : undefined,
        update(cache, { data }) {
          if (!data?.movePlaybookRule) return;

          const ruleRef = cache.writeFragment({
            data: data.movePlaybookRule,
            fragment: Apollo.PlaybookRuleSimpleFragmentDoc,
            fragmentName: Apollo.namedOperations.Fragment.PlaybookRuleSimple,
          });
          if (!ruleRef) return;

          if (fromCategoryId !== categoryId) {
            cache.modify({
              fields: {
                rules: (refs: readonly Reference[] = [], { readField }) =>
                  refs.filter(ref => readField<UUID>('id', ref) !== ruleId),
              },
              id: cache.identify({ __typename: 'PlaybookRuleCategory', id: fromCategoryId }),
            });
          }
          cache.modify({
            fields: {
              rules: (refs: readonly Reference[] = [], { readField }) => {
                const others = refs.filter(ref => readField<UUID>('id', ref) !== ruleId);
                const index = Math.max(0, Math.min(toIndex, others.length));

                return [...others.slice(0, index), ruleRef, ...others.slice(index)];
              },
            },
            id: cache.identify({ __typename: 'PlaybookRuleCategory', id: categoryId }),
          });
        },
        variables: { input },
      });
    },
    [movePlaybookRule]
  );

  return { loading, movePlaybookRule: safeMovePlaybookRule };
}

export function useDeletePlaybookRule() {
  const [deletePlaybookRule, { loading }] = Apollo.useDeletePlaybookRuleMutation();
  const safeDeletePlaybookRule = useCallback(
    (options: { categoryId: UUID; ruleId: UUID }) => {
      const { categoryId, ruleId } = options;

      return deletePlaybookRule({
        update(cache) {
          cache.modify({
            fields: {
              rules: (refs: readonly Reference[] = [], { readField }) =>
                refs.filter(ref => readField<UUID>('id', ref) !== ruleId),
            },
            id: cache.identify({ __typename: 'PlaybookRuleCategory', id: categoryId }),
          });
        },
        variables: { ruleId },
      });
    },
    [deletePlaybookRule]
  );

  return { deletePlaybookRule: safeDeletePlaybookRule, loading };
}

/**
 * The four position mutations all return the whole rule, normalized by id, so
 * the details panel and rule list re-render from the cache with no manual
 * write. Grouped here because they share that behaviour.
 */
export function useAddPlaybookRulePosition() {
  const [addPlaybookRulePosition, { loading }] = Apollo.useAddPlaybookRulePositionMutation();
  const safeAddPlaybookRulePosition = useCallback(
    (input: Apollo.AddPlaybookRulePositionInput) => {
      return addPlaybookRulePosition({ variables: { input } });
    },
    [addPlaybookRulePosition]
  );

  return { addPlaybookRulePosition: safeAddPlaybookRulePosition, loading };
}

export function useUpdatePlaybookRulePosition() {
  const [updatePlaybookRulePosition, { loading }] = Apollo.useUpdatePlaybookRulePositionMutation();
  const safeUpdatePlaybookRulePosition = useCallback(
    (input: Apollo.UpdatePlaybookRulePositionInput) => {
      return updatePlaybookRulePosition({ variables: { input } });
    },
    [updatePlaybookRulePosition]
  );

  return { loading, updatePlaybookRulePosition: safeUpdatePlaybookRulePosition };
}

export function useDeletePlaybookRulePosition() {
  const [deletePlaybookRulePosition, { loading }] = Apollo.useDeletePlaybookRulePositionMutation();
  const safeDeletePlaybookRulePosition = useCallback(
    (input: Apollo.DeletePlaybookRulePositionInput) => {
      return deletePlaybookRulePosition({ variables: { input } });
    },
    [deletePlaybookRulePosition]
  );

  return { deletePlaybookRulePosition: safeDeletePlaybookRulePosition, loading };
}

/**
 * Moves some of a rule's positions, leaving the rest alone.
 *
 * Preferred over {@link useReorderPlaybookRulePositions} for a drag: the drag
 * describes one position moving, and sending only that keeps a second person's
 * concurrent edit to the other positions from being overwritten by a list this
 * client assembled before their change arrived.
 */
/**
 * Moves positions within their rule.
 *
 * Pass `optimisticRule` (the rule as it will read after the move, with the
 * rank the moved position is expected to get) to write it to the cache
 * straight away. The panel orders positions by rank, so a dropped card stays
 * where it was dropped instead of animating home and jumping once the server
 * replies. The server's reply then replaces the guessed rank.
 */
export function useMovePlaybookRulePositions() {
  const [movePlaybookRulePositions, { loading }] = Apollo.useMovePlaybookRulePositionsMutation();
  const safeMovePlaybookRulePositions = useCallback(
    (options: {
      input: Apollo.MovePlaybookRulePositionsInput;
      optimisticRule?: Apollo.PlaybookRuleSimpleFragment;
    }) => {
      const { input, optimisticRule } = options;
      return movePlaybookRulePositions({
        optimisticResponse: optimisticRule
          ? { __typename: 'Mutation', movePlaybookRulePositions: optimisticRule }
          : undefined,
        variables: { input },
      });
    },
    [movePlaybookRulePositions]
  );

  return { loading, movePlaybookRulePositions: safeMovePlaybookRulePositions };
}

export function useReorderPlaybookRulePositions() {
  const [reorderPlaybookRulePositions, { loading }] =
    Apollo.useReorderPlaybookRulePositionsMutation();
  const safeReorderPlaybookRulePositions = useCallback(
    (input: Apollo.ReorderPlaybookRulePositionsInput) => {
      return reorderPlaybookRulePositions({ variables: { input } });
    },
    [reorderPlaybookRulePositions]
  );

  return { loading, reorderPlaybookRulePositions: safeReorderPlaybookRulePositions };
}
